import { useApiErrorToast } from 'app/api/app-api/apiHooksHelpers';
import { ToggleGroupField } from "../../core/components/ToggleGroupField";
import { constantToDisplayName } from "../../core/constantToDisplayName";
import { Field, Form, Formik } from 'formik';
import { RequireAuth, UserRole, UserType } from 'legoland-sdk/dist/experimental';
import { useState } from 'react';
import styled from 'styled-components';
import { Button, FormGroup, Input, Modal, ModalActions, ModalContent, ModalHeader, ModalLayout, Text, ToggleGroup, tombac, useToasts } from 'tombac';
import { ChevronDownIcon, ChevronUpIcon } from 'tombac-icons';
import { useAddUserMutation } from "../api/userManagementApiHooks";
import { number, object, string } from 'yup';
import { useUpdateOrganizationMutation } from "../../organizations/api/organizationsApiHooks";
import { settingsFormDefaultValues } from "../details/SettingsForm/SettingsForm";
import { UserOrganizationFields } from "../list/UserOrganizationFields";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const initialValues = {
  email: '',
  role: UserRole.User,
  organization: {
    id: undefined,
    sapId: ''
  },
  settings: settingsFormDefaultValues,
  type: UserType.Portal
};
const validationSchema = object({
  email: string().email('Email address is invalid').required('Email is required'),
  organization: object({
    id: number().required('Organization is required'),
    sapId: string()
  })
});
const toggleGroupFieldProps = {
  groupContainerProps: {
    $width: '100%'
  }
};
export const CreateUserFormModal = _ref => {
  let {
    isOpen,
    onCancel,
    onSaveSuccess
  } = _ref;
  const addApiErrorToast = useApiErrorToast();
  const {
    addToast
  } = useToasts();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState();
  const {
    mutateAsync: addUser
  } = useAddUserMutation();
  const {
    mutateAsync: updateOrganization
  } = useUpdateOrganizationMutation();
  const handleSubmit = async values => {
    try {
      if (!selectedOrganization.root && !selectedOrganization.sapId && values.organization.sapId) {
        await updateOrganization({
          changes: {
            sapId: values.organization.sapId
          },
          organizationId: values.organization.id
        });
      }
      await addUser({
        user: values
      });
      onSaveSuccess(values.email);
      addToast(`User ${values.email} created`, 'success');
    } catch (error) {
      addApiErrorToast(error, 'Failed to create user');
    }
  };
  return /*#__PURE__*/_jsx(Modal, {
    isOpen: isOpen,
    onRequestClose: onCancel,
    "data-sentry-element": "Modal",
    "data-sentry-component": "CreateUserFormModal",
    "data-sentry-source-file": "CreateUserFormModal.tsx",
    children: /*#__PURE__*/_jsx(Formik, {
      initialValues: initialValues,
      onSubmit: handleSubmit,
      validationSchema: validationSchema,
      "data-sentry-element": "Formik",
      "data-sentry-source-file": "CreateUserFormModal.tsx",
      "data-sentry-element": "Formik",
      children: _ref2 => {
        let {
          errors,
          isSubmitting,
          touched
        } = _ref2;
        return /*#__PURE__*/_jsxs(ModalLayout, {
          $minWidth: "600u",
          $width: "auto",
          as: Form,
          children: [/*#__PURE__*/_jsx(ModalHeader, {
            as: "h2",
            fontSize: 20,
            children: "Create new user"
          }), /*#__PURE__*/_jsxs(ModalContent, {
            children: [/*#__PURE__*/_jsx(Text, {
              altFont: true,
              as: "h3",
              fontWeight: 700,
              children: "User details"
            }), /*#__PURE__*/_jsxs(FormGrid, {
              children: [/*#__PURE__*/_jsx(FormGroup, {
                error: touched.email && errors.email,
                htmlFor: "new-user-email",
                label: "Email",
                children: /*#__PURE__*/_jsx(Field, {
                  as: Input,
                  id: "new-user-email",
                  invalid: Boolean(touched.email && errors.email),
                  name: "email",
                  type: "email"
                })
              }), /*#__PURE__*/_jsxs(RequireAuth, {
                role: UserRole.Admin,
                children: [/*#__PURE__*/_jsx(FormGroup, {
                  $gridColumn: "1",
                  label: "Role",
                  children: /*#__PURE__*/_jsx(ToggleGroupField, {
                    ...toggleGroupFieldProps,
                    name: "role",
                    children: [UserRole.User, UserRole.Distributor, UserRole.Support, UserRole.Admin].map(role => /*#__PURE__*/_jsx(ToggleGroup.Item, {
                      value: role,
                      children: constantToDisplayName(role)
                    }, role))
                  })
                }), /*#__PURE__*/_jsx(FormGroup, {
                  label: "Type",
                  children: /*#__PURE__*/_jsx(ToggleGroupField, {
                    ...toggleGroupFieldProps,
                    name: "type",
                    children: [UserType.Portal, UserType.ApiOnly].map(type => /*#__PURE__*/_jsx(ToggleGroup.Item, {
                      value: type,
                      children: constantToDisplayName(type)
                    }, type))
                  })
                })]
              }), /*#__PURE__*/_jsx(UserOrganizationFields, {
                id: "new-user-organization",
                name: "organization",
                organizationPickerProps: {
                  onOrganizationDetails: setSelectedOrganization
                }
              })]
            }), /*#__PURE__*/_jsxs(SettingsToggle, {
              onClick: () => setIsSettingsOpen(isOpen => !isOpen),
              children: ["Settings", ' ', /*#__PURE__*/_jsx(Text, {
                $display: "inline",
                $mr: "1sp",
                as: "span",
                size: "s",
                variant: "secondary",
                children: "(eg. units, clock format)"
              }), isSettingsOpen ? /*#__PURE__*/_jsx(ChevronUpIcon, {}) : /*#__PURE__*/_jsx(ChevronDownIcon, {})]
            }), isSettingsOpen && /*#__PURE__*/_jsxs(FormGrid, {
              children: [/*#__PURE__*/_jsx(FormGroup, {
                error: touched.settings?.units && errors.settings?.units,
                htmlFor: "new-user-units",
                label: "Units",
                children: /*#__PURE__*/_jsxs(ToggleGroupField, {
                  ...toggleGroupFieldProps,
                  name: "settings.units",
                  children: [/*#__PURE__*/_jsx(ToggleGroup.Item, {
                    value: "KM",
                    children: "KM"
                  }), /*#__PURE__*/_jsx(ToggleGroup.Item, {
                    value: "MI",
                    children: "MI"
                  })]
                })
              }), /*#__PURE__*/_jsx(FormGroup, {
                error: touched.settings?.clockFormat && errors.settings?.clockFormat,
                htmlFor: "new-user-clock-format",
                label: "Clock format",
                children: /*#__PURE__*/_jsxs(ToggleGroupField, {
                  ...toggleGroupFieldProps,
                  name: "settings.clockFormat",
                  children: [/*#__PURE__*/_jsx(ToggleGroup.Item, {
                    value: "24",
                    children: "24"
                  }), /*#__PURE__*/_jsx(ToggleGroup.Item, {
                    value: "12",
                    children: "12"
                  })]
                })
              }), /*#__PURE__*/_jsx(FormGroup, {
                error: touched.settings?.weekStart && errors.settings?.weekStart,
                htmlFor: "new-user-week-start",
                label: "Week start",
                children: /*#__PURE__*/_jsxs(ToggleGroupField, {
                  ...toggleGroupFieldProps,
                  name: "settings.weekStart",
                  children: [/*#__PURE__*/_jsx(ToggleGroup.Item, {
                    value: "MONDAY",
                    children: "Monday"
                  }), /*#__PURE__*/_jsx(ToggleGroup.Item, {
                    value: "SUNDAY",
                    children: "Sunday"
                  })]
                })
              }), /*#__PURE__*/_jsx(FormGroup, {
                error: touched.settings?.dateFormat && errors.settings?.dateFormat,
                htmlFor: "new-user-date-format",
                label: "Date format",
                children: /*#__PURE__*/_jsxs(ToggleGroupField, {
                  ...toggleGroupFieldProps,
                  name: "settings.dateFormat",
                  children: [/*#__PURE__*/_jsx(ToggleGroup.Item, {
                    value: "DDMMYYYY",
                    children: "DD/MM/YYYY"
                  }), /*#__PURE__*/_jsx(ToggleGroup.Item, {
                    value: "MMDDYYYY",
                    children: "MM/DD/YYYY"
                  })]
                })
              })]
            })]
          }), /*#__PURE__*/_jsxs(ModalActions, {
            children: [/*#__PURE__*/_jsx(Button, {
              onClick: onCancel,
              type: "button",
              variant: "flat",
              children: "Cancel"
            }), /*#__PURE__*/_jsx(Button, {
              "data-testid": "create-user-submit",
              busy: isSubmitting,
              type: "submit",
              variant: "primary",
              children: "Create"
            })]
          })]
        });
      }
    })
  });
};
const FormGrid = styled.div.withConfig({
  displayName: "CreateUserFormModal__FormGrid"
})(["align-items:start;display:grid;grid-template-columns:1fr 1fr;gap:", ";margin-top:", ";"], tombac.space(3, 6), tombac.space(2));
const SettingsToggle = styled.h3.withConfig({
  displayName: "CreateUserFormModal__SettingsToggle"
})(["cursor:pointer;margin-top:", ";max-width:max-content;", ""], tombac.space(4), tombac.altText({
  fontWeight: 700
}));